import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { bankService } from './clients/ApiClients';
import { PayableReturn } from './clients/rust-api-client';
import { apiErrorMessage } from './utils/apiErrors';
import { formatCurrency, formatDate } from './utils/bankHelpers';
import { payableStatusColor, payableStatusLabel } from './utils/payableHelpers';

interface VendorPaymentsListProps {
  hoaId: string;
  vendorId: string;
  /** A payment just submitted from the dialog, to show without a reload. */
  justSubmitted?: PayableReturn | null;
}

/**
 * This vendor's ACH payments, newest first — where each one stands with
 * us and with the bank. The listing is the association's; this filters
 * to the vendor on the client, which is fine at the volumes a vendor page
 * sees.
 */
const VendorPaymentsList: React.FC<VendorPaymentsListProps> = ({
  hoaId,
  vendorId,
  justSubmitted,
}) => {
  const [items, setItems] = useState<PayableReturn[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const resp = await bankService.listHoaPayables({
        hoaId,
        direction: 'payment',
      });
      setItems(resp.data.items.filter((p) => p.vendor_id === vendorId));
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't load this vendor's payments."));
    }
  }, [hoaId, vendorId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!justSubmitted) return;
    setItems((current) =>
      current.some((p) => p.id === justSubmitted.id)
        ? current.map((p) => (p.id === justSubmitted.id ? justSubmitted : p))
        : [justSubmitted, ...current]
    );
  }, [justSubmitted]);

  if (error) {
    return (
      <ErrorAlert className="mt-3" onRetry={() => void load()}>
        {error}
      </ErrorAlert>
    );
  }
  if (items.length === 0) return null;

  return (
    <div className="mt-4">
      <Text className="text-sm font-medium">ACH payments</Text>
      <ul className="mt-1 divide-y divide-line">
        {items.slice(0, 8).map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-3 py-1.5 text-sm"
          >
            <div className="min-w-0">
              <span className="truncate">{p.memo ?? 'Payment'}</span>
              <Text className="text-xs">
                {formatDate(p.effective_date)}
                {p.bank_transaction_id
                  ? ` · bank #${p.bank_transaction_id}`
                  : ''}
                {p.status === 'failed' && p.error_message
                  ? ` · ${p.error_message}`
                  : ''}
              </Text>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="tabular-nums">
                {formatCurrency(Number(p.amount))}
              </span>
              <Badge color={payableStatusColor(p.status)}>
                {payableStatusLabel(p)}
              </Badge>
            </div>
          </li>
        ))}
      </ul>
      {items.length > 8 && (
        <Text className="mt-1 text-xs">
          Showing the latest 8 of {items.length}.
        </Text>
      )}
    </div>
  );
};

export default VendorPaymentsList;
